import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Context/AuthContext';
import './DebugTokenPanel.css';

export function DebugTokenPanel() {
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    const { backendToken, accessToken, account, permissions } = useAuth();

    const handleKeyDown = useCallback((e) => {
        if (e.ctrlKey && e.key === 'F9') {
            e.preventDefault();
            setVisible(prev => !prev);
        }
        if (e.key === 'Escape') {
            setVisible(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const copyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(label);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const decodeJwt = (token) => {
        try {
            const payload = token.split('.')[1];
            return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        } catch {
            return null;
        }
    };

    const formatExpiry = (exp) => {
        if (!exp) return 'N/A';
        const date = new Date(exp * 1000);
        const now = new Date();
        const diff = Math.round((date - now) / 60000);
        const timeStr = date.toLocaleTimeString('de-DE');
        if (diff < 0) return `${timeStr} (abgelaufen)`;
        return `${timeStr} (noch ${diff} Min.)`;
    };

    const backendDecoded = backendToken ? decodeJwt(backendToken) : null;
    const graphDecoded = accessToken ? decodeJwt(accessToken) : null;

    return (
        <div className={`debug-panel ${visible ? 'debug-panel--visible' : ''}`}>
            <div className="debug-panel__header">
                <span className="debug-panel__title">
                    🔑 Token Debug Panel
                </span>
                <div className="debug-panel__header-info">
                    <span className="debug-panel__hint">Strg+F9 oder ESC zum Schließen</span>
                    <button className="debug-panel__close" onClick={() => setVisible(false)}>✕</button>
                </div>
            </div>

            <div className="debug-panel__body">

                {/* Account Info */}
                <div className="debug-panel__section">
                    <div className="debug-panel__section-title">👤 Account</div>
                    <div className="debug-panel__grid">
                        <span className="debug-panel__label">Benutzer</span>
                        <span className="debug-panel__value">{account?.name || account?.username || '—'}</span>
                        <span className="debug-panel__label">Username</span>
                        <span className="debug-panel__value">{account?.username || '—'}</span>
                        <span className="debug-panel__label">Rechte</span>
                        <span className="debug-panel__value">
                            {permissions.isAdmin && <span className="debug-panel__badge debug-panel__badge--admin">Admin</span>}
                            {permissions.isWriter && <span className="debug-panel__badge debug-panel__badge--writer">Writer</span>}
                            {permissions.isReader && <span className="debug-panel__badge debug-panel__badge--reader">Reader</span>}
                            {!permissions.isAdmin && !permissions.isWriter && !permissions.isReader && '—'}
                        </span>
                    </div>
                </div>

                {/* Backend Token */}
                <div className="debug-panel__section">
                    <div className="debug-panel__section-title">
                        🔐 Backend Token
                        <span className={`debug-panel__status ${backendToken ? 'debug-panel__status--ok' : 'debug-panel__status--missing'}`}>
                            {backendToken ? '✓ vorhanden' : '✗ nicht vorhanden'}
                        </span>
                    </div>
                    {backendDecoded && (
                        <div className="debug-panel__grid">
                            <span className="debug-panel__label">Scope</span>
                            <span className="debug-panel__value debug-panel__value--mono">{backendDecoded.scp || backendDecoded.scope || '—'}</span>
                            <span className="debug-panel__label">Audience</span>
                            <span className="debug-panel__value debug-panel__value--mono">{backendDecoded.aud || '—'}</span>
                            <span className="debug-panel__label">Ablauf</span>
                            <span className="debug-panel__value">{formatExpiry(backendDecoded.exp)}</span>
                            <span className="debug-panel__label">App ID</span>
                            <span className="debug-panel__value debug-panel__value--mono">{backendDecoded.appid || backendDecoded.azp || '—'}</span>
                        </div>
                    )}
                    {backendToken ? (
                        <div className="debug-panel__token-box">
                            <div className="debug-panel__token-text">{backendToken}</div>
                            <button
                                className={`debug-panel__copy-btn ${copied === 'backend' ? 'debug-panel__copy-btn--copied' : ''}`}
                                onClick={() => copyToClipboard(backendToken, 'backend')}
                            >
                                {copied === 'backend' ? '✓ Kopiert!' : '📋 Kopieren'}
                            </button>
                        </div>
                    ) : (
                        <div className="debug-panel__missing-hint">
                            Kein Backend-Token verfügbar. Scope: <code>api://4af0eab7-a509-4831-8f62-bc2390ce63a8/User</code>
                        </div>
                    )}
                </div>

                {/* Graph Token */}
                <div className="debug-panel__section">
                    <div className="debug-panel__section-title">
                        📊 Graph Token (Microsoft)
                        <span className={`debug-panel__status ${accessToken ? 'debug-panel__status--ok' : 'debug-panel__status--missing'}`}>
                            {accessToken ? '✓ vorhanden' : '✗ nicht vorhanden'}
                        </span>
                    </div>
                    {graphDecoded && (
                        <div className="debug-panel__grid">
                            <span className="debug-panel__label">Scope</span>
                            <span className="debug-panel__value debug-panel__value--mono">{graphDecoded.scp || '—'}</span>
                            <span className="debug-panel__label">Ablauf</span>
                            <span className="debug-panel__value">{formatExpiry(graphDecoded.exp)}</span>
                        </div>
                    )}
                    {accessToken && (
                        <div className="debug-panel__token-box">
                            <div className="debug-panel__token-text">{accessToken}</div>
                            <button
                                className={`debug-panel__copy-btn ${copied === 'graph' ? 'debug-panel__copy-btn--copied' : ''}`}
                                onClick={() => copyToClipboard(accessToken, 'graph')}
                            >
                                {copied === 'graph' ? '✓ Kopiert!' : '📋 Kopieren'}
                            </button>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

